import { useEffect, useState } from "react";
import { getDocument } from "../pdfjs";

// Pixels per PDF point in each drawn background.
const RESOLUTION = 3;

// A template version's backgrounds never change, so each file is drawn once.
const drawnByUrl = new Map<string, Record<number, string>>();

export interface Backgrounds {
  // Every page of the background PDF as an image, keyed by page number.
  images?: Record<number, string>;
  // The file couldn't be read or drawn; the pages show no background.
  failed: boolean;
}

export const useBackgrounds = (url: string | undefined): Backgrounds => {
  const [images, setImages] = useState(url ? drawnByUrl.get(url) : undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Another template's backgrounds never stand in while this one's are drawn.
    setImages(url ? drawnByUrl.get(url) : undefined);
    setFailed(false);
    if (!url || drawnByUrl.has(url)) return;
    let cancelled = false;
    // No eval: a PDF's function code is interpreted, never compiled to JS.
    const task = getDocument({ url, isEvalSupported: false });
    task.promise
      .then(async (pdf) => {
        const pages: Record<number, string> = {};
        for (let number = 1; number <= pdf.numPages; number += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(number);
          const viewport = page.getViewport({ scale: RESOLUTION });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const context = canvas.getContext("2d");
          if (!context) continue;
          await page.render({ canvasContext: context, viewport }).promise;
          pages[number] = canvas.toDataURL("image/png");
        }
        drawnByUrl.set(url, pages);
        if (!cancelled) setImages(pages);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      // Destroying the task mid-render cancels the drawing, so it is released only once drawing ends.
      .finally(() => task.destroy());
    return () => {
      cancelled = true;
    };
  }, [url]);

  return { images, failed };
};
